import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Row, Col, Form, Card } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import SessionCard from "../components/SessionCard";
import { uwinAPI } from "../../../services/uwin.api";
import { decryptOpenSSLAES } from "../components/uwin-decrypt";
import Loader from "../../../components/Loader";
import { getUwinClientToken } from "../../../helpers/common-uwinhelper";
const UwinSession = () => {
	const location = useLocation();
	const sessionRefreshTrigger = useSelector((state) => state.uwin?.sessionRefreshTrigger) || 0;

	const [facilities, setFacilities] = useState([]);
	const [sessionSites, setSessionSites] = useState([]);

	const [selectedFacility, setSelectedFacility] = useState("");
	const [selectedSessionSite, setSelectedSessionSite] = useState("");

	const [sessionData, setSessionData] = useState(null);
	const [loadingSessions, setLoadingSessions] = useState(false);

	/* ================= FETCH FACILITIES ================= */

	useEffect(() => {
		const fetchFacilities = async () => {
			try {
				const token = await getUwinClientToken();
				if (!token) {
					toast.error("UWIN token not available");
					return;
				}

				const res = await uwinAPI.getFacilitySessionSites();

				if (res?.success) {
					setFacilities(
						Array.isArray(res.data)
							? res.data
							: res.data?.facilities || []
					);
				} else {
					toast.error(res?.message || "Failed to fetch facilities");
				}
			} catch {
				toast.error("Something went wrong while fetching facilities");
			}
		};

		fetchFacilities();
	}, []);

	/* ================= RESTORE STATE ================= */

	useEffect(() => {
		if (!location.state) return;

		const {
			selectedFacility,
			selectedSessionSite,
			sessionData,
		} = location.state;

		setSelectedFacility(selectedFacility || "");
		setSelectedSessionSite(selectedSessionSite || "");
		setSessionData(sessionData || null);
	}, [location.state]);

	/* ================= DERIVE SESSION SITES ================= */

	useEffect(() => {
		if (!selectedFacility || !facilities.length) {
			setSessionSites([]);
			return;
		}

		const facility = facilities.find(
			(f) =>
				decryptOpenSSLAES(f.facility_id) ===
				decryptOpenSSLAES(selectedFacility)
		);

		setSessionSites(facility?.session_sites || []);
	}, [selectedFacility, facilities]);

	/* ================= HANDLERS ================= */

	const handleFacilityChange = useCallback((e) => {
		setSelectedFacility(e.target.value);
		setSelectedSessionSite("");
		setSessionData(null);
	}, []);

const handleSessionSiteChange = useCallback((e) => {
	setSelectedSessionSite(e.target.value);
}, []);

useEffect(() => {
	if (!selectedSessionSite) return;

	const fetchSessions = async () => {
		setLoadingSessions(true);
		try {
			const res = await uwinAPI.getSessionBySite({
				session_site_id: selectedSessionSite,
			});

			if (res?.success) setSessionData(res.data);
			else toast.error(res?.message || "Failed to fetch sessions");
		} catch {
			toast.error("Unable to fetch session data");
		} finally {
			setLoadingSessions(false);
		}
	};

	fetchSessions();
}, [selectedSessionSite, sessionRefreshTrigger]);

	const resolvedSelectedFacility = useMemo(() => {
		const match = facilities.find(
			(f) =>
				decryptOpenSSLAES(f.facility_id) ===
				decryptOpenSSLAES(selectedFacility)
		);
		return match?.facility_id || "";
	}, [selectedFacility, facilities]);

	const resolvedSelectedSessionSite = useMemo(() => {
		const match = sessionSites.find(
			(s) =>
				decryptOpenSSLAES(s.session_site_id) ===
				decryptOpenSSLAES(selectedSessionSite)
		);
		return match?.session_site_id || "";
	}, [selectedSessionSite, sessionSites]);

	const hasSessions = useMemo(
		() =>
			sessionData?.ongoing_session_data?.length ||
			sessionData?.upcoming_session_data?.length,
		[sessionData]
	);

	const renderSessions = (data, status) =>
		data.map((item) => (
			<Col md={6} key={item.session_id}>
				<SessionCard
					item={item}
					status={status}
					selectedFacility={selectedFacility}
					selectedSessionSite={selectedSessionSite}
					sessionData={sessionData}
				/>
			</Col>
		));

	return (
		<>
			<div className="container">
				<h2 className="header-session">U-WIN Session Info & Sessions</h2>
			</div>

			<Container className="mt-3">
				{/* FILTERS */}
				<Card className="border-0 shadow-sm mb-3">
					<Card.Body>
						<Row className="g-3">
							<Col md={6}>
								<Form.Label>Select Facility</Form.Label>
								<Form.Select
									value={resolvedSelectedFacility}
									onChange={handleFacilityChange}
								>
									<option value="" disabled>Select Facility</option>
									{facilities.map((f) => (
										<option key={f.facility_id} value={f.facility_id}>
											{f.facility_name}
										</option>
									))}
								</Form.Select>
							</Col>

							<Col md={6}>
								<Form.Label>Session Site</Form.Label>
								<Form.Select
									value={resolvedSelectedSessionSite}
									onChange={handleSessionSiteChange}
									disabled={!sessionSites.length}
								>
									<option value="" disabled>Select Session Site</option>
									{sessionSites.map((s) => (
										<option
											key={s.session_site_id}
											value={s.session_site_id}
										>
											{s.session_site_name}
										</option>
									))}
								</Form.Select>
							</Col>
						</Row>
					</Card.Body>
				</Card>

				{loadingSessions ? (
					<Loader />
				) : hasSessions ? (
					<>
						{sessionData?.ongoing_session_data?.length > 0 && (
							<Row className="g-4">
								{renderSessions(sessionData.ongoing_session_data, "ONGOING")}
							</Row>
						)}
						{sessionData?.upcoming_session_data?.length > 0 && (
							<Row className="g-4 mt-1">{renderSessions(sessionData.upcoming_session_data.reverse(), "UPCOMING")}</Row>
						)}
					</>
				) : (
					<div className="text-center text-muted mt-4">
						No sessions found
					</div>
				)}
			</Container>
		</>
	);
};

export default UwinSession;
